import { Dialog, DialogTitle, DialogContent, Tabs, Tab, List, ListItem, Typography, Box, IconButton, CircularProgress, LinearProgress, Tooltip, alpha } from "@mui/material";
import { useMemo, useState } from "react";
import { useTransferQueue } from "./app/transferQueue";
import { humanReadableSize } from "./app/utils";
import { CloudUpload, CloudDownload, Close, ErrorOutline, CheckCircleOutline } from "@mui/icons-material";

interface ProgressDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function ProgressDialog({
  open,
  onClose,
}: ProgressDialogProps) {
  const [tab, setTab] = useState(0);
  const transferQueue = useTransferQueue();

  const tasks = useMemo(() => {
    const taskType = tab === 0 ? "upload" : "download";
    return Object.values(transferQueue).filter(
      (task) => task.type === taskType
    );
  }, [tab, transferQueue]);

  const stats = useMemo(() => {
    const active = tasks.filter(t => t.status === 'in-progress' || t.status === 'pending').length;
    const completed = tasks.filter(t => t.status === 'completed').length;
    const failed = tasks.filter(t => t.status === 'failed').length;
    return { active, completed, failed };
  }, [tasks]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 2px 6px 2px rgba(60,64,67,0.15)',
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {tab === 0 ? (
            <CloudUpload sx={{ color: 'primary.main' }} />
          ) : (
            <CloudDownload sx={{ color: 'primary.main' }} />
          )}
          <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.25rem' }}>
            Transfer Progress
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <Tabs
        value={tab}
        onChange={(_, newTab) => setTab(newTab)}
        sx={{
          px: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTab-root': {
            flexBasis: '50%',
            textTransform: 'none',
            fontWeight: 500,
          },
        }}
      >
        <Tab label="Uploads" />
        <Tab label="Downloads" />
      </Tabs>

      {stats.active > 0 && (
        <Box sx={{ px: 3, pt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {stats.active} active • {stats.completed} completed • {stats.failed} failed
            </Typography>
          </Box>
        </Box>
      )}

      {tasks.length === 0 ? (
        <DialogContent>
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: 'rgba(26, 115, 232, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              {tab === 0 ? (
                <CloudUpload sx={{ fontSize: 32, color: 'primary.main' }} />
              ) : (
                <CloudDownload sx={{ fontSize: 32, color: 'primary.main' }} />
              )}
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 400 }}>
              No {tab === 0 ? 'uploads' : 'downloads'} yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.8125rem' }}>
              {tab === 0 ? 'Upload files to see them here' : 'Download files to see them here'}
            </Typography>
          </Box>
        </DialogContent>
      ) : (
        <DialogContent sx={{ p: 0, maxHeight: 400 }}>
          <List sx={{ px: 1, py: 1 }}>
            {tasks.map((task) => {
              const progress = task.total > 0 ? Math.min((task.loaded / task.total) * 100, 100) : 0;

              return (
                <ListItem
                  key={task.id}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: 1,
                    '&:hover': {
                      backgroundColor: '#f8f9fa',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: 400,
                          fontSize: '0.875rem',
                        }}
                      >
                        {task.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {humanReadableSize(task.loaded)} / {humanReadableSize(task.total)}
                        {task.status === 'in-progress' && ` • ${Math.round(progress)}%`}
                      </Typography>
                    </Box>

                    <Box sx={{ flexShrink: 0 }}>
                      {task.status === "failed" ? (
                        <Tooltip title={task.error?.message || "Upload failed"}>
                          <ErrorOutline color="error" />
                        </Tooltip>
                      ) : task.status === "completed" ? (
                        <CheckCircleOutline sx={{ color: 'success.main' }} />
                      ) : task.status === "in-progress" ? (
                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                          <CircularProgress
                            variant="determinate"
                            size={32}
                            value={progress}
                            thickness={4}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              bottom: 0,
                              right: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography variant="caption" fontSize={10} fontWeight={600}>
                              {Math.round(progress)}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <CircularProgress size={24} />
                      )}
                    </Box>
                  </Box>

                  {task.status === "in-progress" && (
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: 'rgba(26, 115, 232, 0.1)',
                      }}
                    />
                  )}
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
      )}
    </Dialog>
  );
}
